import { getrelatedservice } from '@/app/lib/relatedservice';
import { blogs } from '@/data/blogs';
import { getPublishedCaseStudies } from '@/data/caseStudies';
import { getCategoryTitle } from '@/lib/teamCategoryMap';
import { BASE_URL } from './constants';
import type { BreadcrumbItem } from './schemas';

export const HOME_CRUMB: BreadcrumbItem = { name: 'Home', url: BASE_URL };

/** Paths where breadcrumbs should not render (transactional or utility flows). */
export const BREADCRUMB_EXCLUDED_PREFIXES = [
  '/booking-report',
  '/payments',
  '/invoices',
  '/registration',
];

export const STATIC_ROUTE_LABELS: Record<string, string> = {
  '/about-us': 'About Us',
  '/why-choose-us': 'Why Choose Us',
  '/services': 'Services',
  '/teams': 'Our Team',
  '/gallery': 'Gallery',
  '/case-studies': 'Case Studies',
  '/contact-us': 'Contact Us',
  '/products': 'Products',
  '/products/speechsync': 'SpeechSync',
  '/products/speeching-cards': 'Speeching Cards',
  '/products/sogpod': 'SoGPOD',
  '/products/neurocular': 'Neurocular',
  '/blogs': 'Blogs',
  '/faq': 'FAQ',
  '/reviews': 'Reviews',
  '/careers': 'Careers',
  '/terms-and-conditions': 'Terms and Conditions',
  '/privacy-policy': 'Privacy Policy',
  '/neurocular/policies/privacy-policy': 'Neurocular Privacy Policy',
  '/neurocular/policies/terms-and-conditions': 'Neurocular Terms and Conditions',
  '/booking': 'Book an Appointment',
  '/search': 'Search',
  '/payment-success': 'Payment Successful',
  '/speech-delay-in-children-dubai': 'Speech Delay in Children Dubai',
  '/speech-delay-in-2-year-old': 'Speech Delay in 2-Year-Olds',
  '/speech-delay-in-3-year-old': 'Speech Delay in 3-Year-Olds',
  '/speech-delay-in-4-year-old': 'Speech Delay in 4-Year-Olds',
  '/child-understands-but-doesnt-speak': "Child Understands but Doesn't Speak",
  '/child-speech-difficult-to-understand': "Child's Speech Difficult to Understand",
  '/childhood-apraxia-of-speech-dubai': 'Childhood Apraxia of Speech Dubai',
  '/sensory-processing-difficulties-dubai': 'Sensory Processing Difficulties Dubai',
  '/feeding-difficulties-in-children-dubai': 'Feeding Difficulties in Children Dubai',
  '/social-communication-difficulties-in-children-dubai': 'Social Communication Difficulties in Children Dubai',
  '/what-is-occupational-therapy': 'What Is Occupational Therapy',
  '/occupational-therapy-for-autism': 'Occupational Therapy for Autism',
  '/speech-therapy-for-autism': 'Speech Therapy for Autism',
  '/language-delay-in-children-dubai': 'Language Delay in Children',
  '/aac-therapy-for-children-dubai': 'AAC Therapy for Children',
  '/pediatric-speech-assessment-dubai': 'Pediatric Speech Assessment',
  '/speech-delay-vs-language-delay': 'Speech Delay vs Language Delay',
  '/speech-therapy-vs-occupational-therapy': 'Speech Therapy vs Occupational Therapy',
  '/speech-delay-vs-autism': 'Speech Delay vs Autism',
  '/aba-therapy-vs-speech-therapy': 'ABA Therapy vs Speech Therapy',
  '/feeding-assessment-dubai': 'Feeding Assessment',
  '/drooling-management-dubai': 'Drooling Management',
  '/down-syndrome-speech-therapy-dubai': 'Down Syndrome Speech Therapy',
  '/autism-early-intervention-dubai': 'Autism Early Intervention',
  '/motor-delays-in-children-dubai': 'Motor Delays in Children',
};

const STATIC_SERVICE_LABELS: Record<string, string> = {
  'occupational-therapy': 'Occupational Therapy',
  'speech-language-therapy': 'Speech & Language Therapy',
  'child-speech-development': 'Child Speech Development',
  'physical-therapy': 'Physical Therapy',
  'aba-therapy': 'ABA Therapy',
  'school-readiness-program': 'School Readiness Program',
  'sports-fitness-club': 'Sports & Fitness Club',
  'feeding-therapy': 'Feeding Therapy',
  'group-therapy': 'Group Therapy',
  'neuro-integrative-therapy': 'Neuro Integrative Therapy',
  'intensive-therapy-programs': 'Intensive Therapy Programs',
};

export function slugToLabel(slug: string): string {
  return slug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function toRelativePath(url: string): string {
  return url.replace(BASE_URL, '') || '/';
}

export function shouldShowBreadcrumbs(pathname: string): boolean {
  if (!pathname || pathname === '/') return false;
  return !BREADCRUMB_EXCLUDED_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

function getServiceLabel(slug: string): string {
  if (STATIC_SERVICE_LABELS[slug]) return STATIC_SERVICE_LABELS[slug];
  const service = getrelatedservice().find((s) => s.slug === slug);
  return service?.title ?? slugToLabel(slug);
}

function getBlogLabel(slug: string): string | undefined {
  return blogs.find((b) => b.slug === slug)?.title;
}

/**
 * Resolve breadcrumb items from a pathname. Pass `currentLabel` for dynamic
 * final segments (team members, CMS services, blog posts).
 */
export function resolveBreadcrumbs(
  pathname: string,
  currentLabel?: string
): BreadcrumbItem[] {
  if (!shouldShowBreadcrumbs(pathname)) return [];

  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return [];

  const items: BreadcrumbItem[] = [HOME_CRUMB];
  let path = '';

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    path += `/${segment}`;
    const isLast = i === segments.length - 1;

    let label: string;

    if (isLast && currentLabel) {
      label = currentLabel;
    } else if (STATIC_ROUTE_LABELS[path]) {
      label = STATIC_ROUTE_LABELS[path];
    } else if (segments[0] === 'services' && i === 1) {
      label = getServiceLabel(segment);
    } else if (segments[0] === 'teams' && segment === 'member') {
      label = 'Team Member';
    } else if (segments[0] === 'teams' && i === 1 && segment !== 'member') {
      label = getCategoryTitle(segment);
    } else if (segments[0] === 'blogs' && i === 1) {
      label = getBlogLabel(segment) ?? slugToLabel(segment);
    } else if (segments[0] === 'case-studies' && i === 1) {
      const story = getPublishedCaseStudies().find((item) => item.slug === segment);
      label = story ? (story.childFirstName ? `${story.childFirstName}'s story` : "A parent story") : slugToLabel(segment);
    } else {
      label = slugToLabel(segment);
    }

    items.push({ name: label, url: `${BASE_URL}${path}` });
  }

  return items;
}

export function buildTeamMemberBreadcrumbs(
  memberName: string,
  categorySlug: string,
  memberSlug: string
): BreadcrumbItem[] {
  return [
    HOME_CRUMB,
    { name: 'Our Team', url: `${BASE_URL}/teams` },
    {
      name: getCategoryTitle(categorySlug),
      url: `${BASE_URL}/teams/${categorySlug}`,
    },
    { name: memberName, url: `${BASE_URL}/teams/member/${memberSlug}` },
  ];
}
